import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  endOfMonth,
  format,
  isValid,
  parse,
} from 'date-fns';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { Appointment } from '@/types';
import { Client as ClientType } from '@/types/client';
import AppointmentDetails from '@/components/appointments/AppointmentDetails';
import AppointmentForm, { AppointmentFormValues } from '@/components/appointments/AppointmentForm';
import AppointmentListCard from '@/components/appointments/AppointmentListCard';
import PaginationControls from '@/components/shared/PaginationControls';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import ScheduleAppointmentDialog from '@/components/appointments/ScheduleAppointmentDialog';
import CancelAppointmentDialog, {
  type CancelAppointmentScope,
} from '@/components/appointments/CancelAppointmentDialog';
import Modal from '@/components/shared/Modal';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { ActionTour, type ActionTourStep } from '@/components/tutorial/ActionTour';
import appointmentCalendarFilterIcon from '@/assets/images/appointments/appointment-calendar-filter.svg';
import appointmentDateIcon from '@/assets/images/appointments/appointment-date.svg';
import appointmentProfileIcon from '@/assets/images/appointments/appointment-profile.svg';
import appointmentThisWeekOnlineSessionsIcon from '@/assets/images/appointments/appointment-this-week-online-sessions.svg';
import { useAppointmentCalendar, useAppointmentDialog } from '@/hooks/appointments';
import {
  getAppointmentsDashboard,
  mapAppointmentRowsFromApi,
  type AppointmentsDashboardAppointmentType,
  type AppointmentsDashboardStats,
  type AppointmentsListPagination,
} from '@/lib/api/appointments';
import {
  createAppointmentAction,
  deleteAppointmentAction,
  editAppointmentAction,
  getAppointmentFormDefaultValues,
  shouldHideAppointmentJoinRescheduleCancel,
  canJoinAppointment,
} from '@/utils/appointments';
import { getClientDisplayName, getIanaTimeZone } from '@/utils/appointments/schedule-dialog-utils';
import { showErrorToast } from '@/utils/toast-utils';

type AppointmentTab = 'all' | 'upcoming' | 'past';
type ModeFilter = 'all' | 'in_person' | 'online';
interface AppointmentListItem {
  id: string;
  appointment: Appointment;
  clientName: string;
  clientEmail: string;
  title: string;
  startTime: Date;
  endTime: Date;
  status: string;
  mode: Exclude<ModeFilter, 'all'>;
  modeLabel: string;
  meetingLink: string;
}

type AppointmentRecord = Appointment & {
  appointmentType?: unknown;
  mode?: unknown;
  location?: unknown;
  startTime?: unknown;
  start_time?: unknown;
  endTime?: unknown;
  end_time?: unknown;
  clientId?: unknown;
  clientName?: unknown;
  clientEmail?: unknown;
  status?: unknown;
  title?: unknown;
  description?: unknown;
  meetingLink?: unknown;
  meeting_link?: unknown;
  recurringDetails?: {
    isModified?: boolean;
    isParent?: boolean;
  } | null;
};

const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '26px', md: '38px' },
  lineHeight: { xs: '115%', md: '100%' },
  letterSpacing: 0,
  color: '#1e293b',
} as const;

const pageSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: { xs: '20px', sm: '22px' },
  color: '#667085',
} as const;

const parseDateValue = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getAppointmentMode = (rawAppointment: AppointmentRecord): Exclude<ModeFilter, 'all'> => {
  const type = String(rawAppointment?.appointmentType || rawAppointment?.mode || '').toLowerCase();
  const location = String(rawAppointment?.location || '').toLowerCase();

  if (type === 'offline' || type === 'in_person' || location.includes('person')) {
    return 'in_person';
  }

  return 'online';
};

const getAppointmentModeLabel = (mode: Exclude<ModeFilter, 'all'>): string =>
  mode === 'in_person' ? 'In-Person' : 'Video Call';

const tabOptions: Array<{ key: AppointmentTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
];

const CLIENT_BY_ID_FETCH_CONCURRENCY = 8;

function collectClientIdsFromAppointments(appointments: Appointment[]): Set<string> {
  const ids = new Set<string>();
  for (const appointment of appointments) {
    const raw = appointment as unknown as { clientId?: unknown; client_id?: unknown };
    const id = String(raw.clientId ?? raw.client_id ?? (appointment as Appointment).clientId ?? '').trim();
    if (id) ids.add(id);
  }
  return ids;
}

function mapAppointmentsToListItems(
  appointments: Appointment[],
  clientsById: Map<string, ClientType>,
): AppointmentListItem[] {
  return appointments
    .map((appointment, index) => {
      const rawAppointment = appointment as AppointmentRecord;
      const startTime = parseDateValue(
        rawAppointment?.startTime || rawAppointment?.start_time || appointment.date,
      );

      if (!startTime) {
        return null;
      }

      const defaultDuration =
        Number.isFinite(appointment.duration) && appointment.duration > 0 ? appointment.duration : 60;
      const endFallback = new Date(startTime.getTime() + defaultDuration * 60_000);
      const endTime =
        parseDateValue(rawAppointment?.endTime || rawAppointment?.end_time) || endFallback;

      const clientId = String(rawAppointment?.clientId || appointment.clientId || '');
      const client = clientsById.get(clientId);

      const status = String(rawAppointment?.status || appointment.status || 'active').toLowerCase();
      const mode = getAppointmentMode(rawAppointment);

      return {
        id: String(appointment.id || `appointment-${index}`),
        appointment,
        clientName: client ? getClientDisplayName(client) : String(rawAppointment?.clientName || 'Unknown Client'),
        clientEmail: client?.email || String(rawAppointment?.clientEmail || ''),
        title: String(rawAppointment?.title || appointment.type || 'Appointment').trim() || 'Appointment',
        startTime,
        endTime,
        status,
        mode,
        modeLabel: getAppointmentModeLabel(mode),
        meetingLink: String(rawAppointment?.meetingLink || rawAppointment?.meeting_link || '').trim(),
      };
    })
    .filter((item): item is AppointmentListItem => Boolean(item))
    .filter((item) => item.status !== 'cancelled');
}

/** Filters passed to dashboard list fetches. */
type DashboardListParams = {
  appointmentType: AppointmentsDashboardAppointmentType;
  dateRange?: { from: string; to: string };
};

const APPOINTMENTS_LIST_PAGE_SIZE = 20;

type AppointmentsSummaryStats = {
  todayTotal: number;
  thisWeekInPerson: number;
  thisWeekScheduled: number;
  thisWeekOnline: number;
};

const DEFAULT_APPOINTMENTS_SUMMARY_STATS: AppointmentsSummaryStats = {
  todayTotal: 0,
  thisWeekInPerson: 0,
  thisWeekScheduled: 0,
  thisWeekOnline: 0,
};

const appointmentsActionTourSteps: ActionTourStep[] = [
  {
    id: 'appointments-new',
    title: 'New Appointment',
    description: 'Use this to schedule a session. The scheduler lets you choose the client, session type, date, time, location, and optional questionnaires before sending it.',
  },
  {
    id: 'appointments-calendar',
    title: 'Calendar View',
    description: 'Open the calendar page when you need to see appointments by day, week, or month instead of the compact list.',
  },
  {
    id: 'appointments-tabs',
    title: 'Appointment Status',
    description: 'Switch between all appointments, upcoming sessions, and past sessions. The number beside each tab shows how many appointments are in that group.',
  },
  {
    id: 'appointments-filters',
    title: 'Mode and Date Filters',
    description: 'Filter the list by online or in-person sessions, or use the calendar icon to narrow appointments to a date range.',
  },
  {
    id: 'appointments-list',
    title: 'Appointment Rows',
    description: 'Open a row to view the appointment in the client profile. Row actions let you reschedule, join an online session, or cancel when those actions are available.',
  },
];

function mapDashboardStatsToSummary(stats?: AppointmentsDashboardStats | null): AppointmentsSummaryStats {
  if (!stats) return DEFAULT_APPOINTMENTS_SUMMARY_STATS;
  return {
    todayTotal: stats.today?.totalAppointments ?? 0,
    thisWeekInPerson: stats.thisWeek?.inPersonSessions ?? 0,
    thisWeekScheduled: stats.thisWeek?.scheduledTotal ?? 0,
    thisWeekOnline: stats.thisWeek?.onlineSessions ?? 0,
  };
}

const defaultListPagination = (page = 1): AppointmentsListPagination => ({
  page,
  limit: APPOINTMENTS_LIST_PAGE_SIZE,
  total: 0,
  totalPages: 1,
});

/** Single list fetch for one tab + current filters (used on load, tab change, and filter change). */
async function fetchAppointmentsForTabQuery(
  tab: AppointmentTab,
  params: DashboardListParams,
  page: number,
  limit: number,
): Promise<{
  rows: Appointment[];
  count: number;
  stats?: AppointmentsDashboardStats;
  tabCounts?: { all: number; upcoming: number; past: number };
  pagination: AppointmentsListPagination;
}> {
  const tz = getIanaTimeZone();
  const { appointmentType, dateRange } = params;
  const dashOpts = { appointmentType, ...(dateRange ?? {}) };

  const res = await getAppointmentsDashboard({ tab, timezone: tz, page, limit, ...dashOpts });
  return {
    rows: mapAppointmentRowsFromApi(res.appointments as unknown[]),
    count: res.count,
    stats: res.stats,
    tabCounts: res.tabCounts,
    pagination: res.pagination ?? defaultListPagination(page),
  };
}

async function fetchAppointmentsDashboardStats(
  params: DashboardListParams,
): Promise<AppointmentsSummaryStats> {
  const tz = getIanaTimeZone();
  const { appointmentType, dateRange } = params;
  const dashOpts = { appointmentType, ...(dateRange ?? {}) };
  const res = await getAppointmentsDashboard({
    tab: 'all',
    timezone: tz,
    page: 1,
    limit: 1,
    ...dashOpts,
  });
  return mapDashboardStatsToSummary(res.stats);
}


const AppointmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<ClientType[]>([]);
  const [activeTab, setActiveTab] = useState<AppointmentTab>('all');
  const activeTabRef = useRef<AppointmentTab>(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const [listDateRange, setListDateRange] = useState<DateRange | undefined>();
  const [dateRangeFilterOpen, setDateRangeFilterOpen] = useState(false);
  const [rangePickerDraft, setRangePickerDraft] = useState<DateRange | undefined>();
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [schedulePrefill, setSchedulePrefill] = useState<{ date?: Date; time?: string } | undefined>();
  const [appointmentToReschedule, setAppointmentToReschedule] = useState<string | null>(null);
  const [cancelAppointmentItem, setCancelAppointmentItem] = useState<Appointment | null>(null);
  const [cancelAppointmentFeedback, setCancelAppointmentFeedback] = useState<{ title: string; message: string } | null>(null);
  const [appointmentModalFeedback, setAppointmentModalFeedback] = useState<{ title: string; message: string } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const {
    selectedDate,
    selectedTime,
    selectedClient,
    setSelectedDate,
    setSelectedTime,
  } = useAppointmentCalendar();

  const [listAppointments, setListAppointments] = useState<Appointment[]>([]);
  const [summaryStats, setSummaryStats] = useState<AppointmentsSummaryStats>(
    DEFAULT_APPOINTMENTS_SUMMARY_STATS,
  );
  const [tabCountsBadge, setTabCountsBadge] = useState<Record<AppointmentTab, number>>({
    all: 0,
    upcoming: 0,
    past: 0,
  });
  const [loadingList, setLoadingList] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPagination, setListPagination] = useState<AppointmentsListPagination>(() =>
    defaultListPagination(1),
  );

  const dashboardDateRange = useMemo((): { from: string; to: string } | undefined => {
    const from = listDateRange?.from;
    const to = listDateRange?.to;
    if (!from || !to) return undefined;
    const start = from.getTime() <= to.getTime() ? from : to;
    const end = from.getTime() <= to.getTime() ? to : from;
    return { from: format(start, 'yyyy-MM-dd'), to: format(end, 'yyyy-MM-dd') };
  }, [listDateRange]);

  const dashboardAppointmentType = useMemo((): AppointmentsDashboardAppointmentType => {
    if (modeFilter === 'in_person') return 'offline';
    if (modeFilter === 'online') return 'online';
    return 'all';
  }, [modeFilter]);

  const dashboardListFetchParams = useMemo(
    () => ({
      appointmentType: dashboardAppointmentType,
      dateRange: dashboardDateRange,
    }),
    [dashboardAppointmentType, dashboardDateRange],
  );

  const dashboardListFetchParamsRef = useRef(dashboardListFetchParams);
  dashboardListFetchParamsRef.current = dashboardListFetchParams;

  const applySingleTabFetchResult = useCallback((
    tab: AppointmentTab,
    rows: Appointment[],
    count: number,
    pagination: AppointmentsListPagination,
    tabCounts?: { all: number; upcoming: number; past: number },
    stats?: AppointmentsDashboardStats,
  ) => {
    setListAppointments(rows);
    setListPagination(pagination);
    if (tabCounts) {
      setTabCountsBadge((prev) => ({
        ...prev,
        all: tabCounts.all,
        upcoming: tabCounts.upcoming,
        past: tabCounts.past,
      }));
    } else {
      setTabCountsBadge((prev) => ({ ...prev, [tab]: count }));
    }
    if (stats) {
      setSummaryStats(mapDashboardStatsToSummary(stats));
    }
  }, []);

  const listPageRef = useRef(listPage);
  listPageRef.current = listPage;

  const refreshAppointmentData = useCallback(async () => {
    try {
      setLoadingList(true);
      const tab = activeTabRef.current;
      const p = dashboardListFetchParamsRef.current;
      const page = listPageRef.current;
      const { rows, count, stats, tabCounts, pagination } = await fetchAppointmentsForTabQuery(
        tab,
        p,
        page,
        APPOINTMENTS_LIST_PAGE_SIZE,
      );
      applySingleTabFetchResult(tab, rows, count, pagination, tabCounts, stats);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showErrorToast(error, 'Failed to load appointments');
    } finally {
      setLoadingList(false);
    }
  }, [applySingleTabFetchResult]);

  const refreshSummaryStats = useCallback(async () => {
    try {
      const stats = await fetchAppointmentsDashboardStats(dashboardListFetchParamsRef.current);
      setSummaryStats(stats);
    } catch (error) {
      console.error('Error fetching appointment stats:', error);
    }
  }, []);

  const reloadAppointments = useCallback(async () => {
    await Promise.all([refreshAppointmentData(), refreshSummaryStats()]);
  }, [refreshAppointmentData, refreshSummaryStats]);

  const listFilterKey = useMemo(
    () =>
      `${activeTab}|${dashboardListFetchParams.appointmentType}|${dashboardListFetchParams.dateRange?.from ?? ''}|${dashboardListFetchParams.dateRange?.to ?? ''}`,
    [activeTab, dashboardListFetchParams],
  );
  const listFilterKeyRef = useRef(listFilterKey);

  useEffect(() => {
    const filtersChanged = listFilterKeyRef.current !== listFilterKey;
    listFilterKeyRef.current = listFilterKey;

    if (filtersChanged && listPage !== 1) {
      setListPage(1);
      return;
    }

    const pageToLoad = filtersChanged ? 1 : listPage;
    let cancelled = false;

    const run = async () => {
      setLoadingList(true);
      try {
        const { rows, count, stats, tabCounts, pagination } = await fetchAppointmentsForTabQuery(
          activeTab,
          dashboardListFetchParams,
          pageToLoad,
          APPOINTMENTS_LIST_PAGE_SIZE,
        );
        if (cancelled) return;
        applySingleTabFetchResult(activeTab, rows, count, pagination, tabCounts, stats);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching appointments:', error);
          showErrorToast(error, 'Failed to load appointments');
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [listFilterKey, listPage, activeTab, dashboardListFetchParams, applySingleTabFetchResult]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const stats = await fetchAppointmentsDashboardStats(dashboardListFetchParams);
        if (!cancelled) {
          setSummaryStats(stats);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching appointment stats:', error);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [dashboardListFetchParams]);

  const handleListPageChange = useCallback((page: number) => {
    setListPage(page);
  }, []);

  const listPaginationSummary = useMemo(() => {
    const { page, limit, total, totalPages } = listPagination;
    if (total <= 0) return '';
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);
    return `Showing ${start}-${end} of ${total} appointments • Page ${page} of ${totalPages}`;
  }, [listPagination]);

  const {
    createDialogOpen,
    selectedAppointment,
    appointmentDetailOpen,
    checkingCalendarStatus,
    updateCreateDialogState,
    setSelectedAppointment,
    setAppointmentDetailOpen,
  } = useAppointmentDialog(selectedDate, setSelectedDate, selectedTime, setSelectedTime, {
    syncCreateFromUrl: false,
  });

  const handleCreateAppointment = async (data: AppointmentFormValues) => {
    const scope = data.updateScope || 'this_only';
    setAppointmentModalFeedback(null);
    await createAppointmentAction(
      data,
      selectedAppointment,
      updateCreateDialogState,
      setSelectedAppointment,
      setSelectedTime,
      reloadAppointments,
      scope,
      {
        suppressSuccessToast: true,
        onError: (title, message) => setAppointmentModalFeedback({ title, message }),
      },
    );
  };

  const handleDeleteAppointment = async (
    deleteScope: 'this_only' | 'this_and_following' | 'all' = 'this_only',
    appointmentOverride?: Appointment | null,
  ) => {
    const appointmentToDelete = appointmentOverride || selectedAppointment;

    updateCreateDialogState(false);
    setSelectedTime(undefined);
    setSelectedAppointment(null);

    if (appointmentToDelete) {
      return await deleteAppointmentAction(
        appointmentToDelete,
        setAppointmentDetailOpen,
        setSelectedAppointment,
        reloadAppointments,
        deleteScope,
        appointmentOverride
          ? {
              suppressSuccessToast: true,
              onError: (title, message) => setCancelAppointmentFeedback({ title, message }),
            }
          : undefined,
      );
    }
    return false;
  };

  const handleEditAppointment = () => {
    editAppointmentAction(
      selectedAppointment,
      setAppointmentDetailOpen,
      updateCreateDialogState,
    );
  };

  const clientsById = useMemo(
    () => new Map(clients.map((client) => [client.id.toString(), client])),
    [clients],
  );

  const cancelAppointmentClientName = useMemo(() => {
    if (!cancelAppointmentItem) return undefined;
    const client = clientsById.get(String(cancelAppointmentItem.clientId));
    return client ? getClientDisplayName(client) : undefined;
  }, [cancelAppointmentItem, clientsById]);

  const cancelAppointmentRecord = cancelAppointmentItem as AppointmentRecord | null;


  const appointmentItems = useMemo<AppointmentListItem[]>(
    () => mapAppointmentsToListItems(listAppointments, clientsById),
    [listAppointments, clientsById],
  );

  const visibleAppointments = useMemo(() => {
    const scoped = appointmentItems;
    const nowTimestamp = Date.now();

    if (activeTab === 'past') {
      return scoped.sort((left, right) => right.startTime.getTime() - left.startTime.getTime());
    }

    if (activeTab === 'all') {
      const upcomingItems = scoped
        .filter((item) => item.startTime.getTime() >= nowTimestamp)
        .sort((left, right) => left.startTime.getTime() - right.startTime.getTime());

      const pastItems = scoped
        .filter((item) => item.startTime.getTime() < nowTimestamp)
        .sort((left, right) => right.startTime.getTime() - left.startTime.getTime());

      return [...upcomingItems, ...pastItems];
    }

    return scoped.sort((left, right) => left.startTime.getTime() - right.startTime.getTime());
  }, [activeTab, appointmentItems]);

  const selectedAppointmentClient = useMemo(() => {
    if (!selectedAppointment) return undefined;

    const foundClient = clients.find(
      (client) => client.id.toString() === selectedAppointment.clientId.toString(),
    );

    if (!foundClient) return undefined;

    const numericId = Number(foundClient.id);

    return {
      id: Number.isFinite(numericId) ? numericId : 0,
      name: getClientDisplayName(foundClient),
      email: foundClient.email,
    };
  }, [clients, selectedAppointment]);

  const handleOpenDetailsFromList = (appointment: Appointment) => {
    const raw = appointment as unknown as { clientId?: unknown; client_id?: unknown };
    const clientId = String(raw.clientId ?? raw.client_id ?? '').trim();
    if (clientId) {
      navigate(
        `/clients/${clientId}/details-v2/appointments?session=${encodeURIComponent(String(appointment.id))}`,
      );
      return;
    }
    setSelectedAppointment(appointment);
    setAppointmentDetailOpen(true);
  };

  const handleRescheduleFromList = (event: React.MouseEvent, appointment: Appointment) => {
    event.stopPropagation();
    setAppointmentDetailOpen(false);
    setSelectedAppointment(null);
    setAppointmentToReschedule(String(appointment.id));
    setIsScheduleDialogOpen(true);
  };

  const handleCancelFromList = (event: React.MouseEvent, appointment: Appointment) => {
    event.stopPropagation();
    setCancelAppointmentFeedback(null);
    setCancelAppointmentItem(appointment);
  };

  const handleConfirmCancelAppointment = async (scope: CancelAppointmentScope = 'this_only') => {
    if (!cancelAppointmentItem) return;
    setIsCancelling(true);
    try {
      setCancelAppointmentFeedback(null);
      const wasCancelled = await handleDeleteAppointment(scope, cancelAppointmentItem);
      if (wasCancelled) {
        setCancelAppointmentItem(null);
      }
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOpenNewAppointmentFlow = (prefill?: { date?: Date; time?: string }) => {
    updateCreateDialogState(false);
    setAppointmentModalFeedback(null);
    setAppointmentToReschedule(null);
    setSchedulePrefill(prefill);
    setIsScheduleDialogOpen(true);
  };

  useEffect(() => {
    if (searchParams.get('create') !== 'true') return;

    const dateParam = searchParams.get('date');
    const timeParam = searchParams.get('time');

    let prefillDate: Date | undefined;
    if (dateParam) {
      const parsed = parse(dateParam, 'yyyy-MM-dd', new Date());
      if (isValid(parsed)) {
        prefillDate = parsed;
      }
    }

    handleOpenNewAppointmentFlow({
      date: prefillDate,
      time: timeParam ?? undefined,
    });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    nextParams.delete('date');
    nextParams.delete('time');
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleOpenCalendarView = () => {
    navigate('/appointments/calendar');
  };

  const listDateRangeShortLabel = useMemo(() => {
    if (!listDateRange?.from || !listDateRange?.to) return '';
    const start =
      listDateRange.from.getTime() <= listDateRange.to.getTime() ? listDateRange.from : listDateRange.to;
    const end =
      listDateRange.from.getTime() <= listDateRange.to.getTime() ? listDateRange.to : listDateRange.from;
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }, [listDateRange]);

  const handleListDateRangePopoverChange = (open: boolean) => {
    setDateRangeFilterOpen(open);
    if (open) {
      setRangePickerDraft(listDateRange);
    }
  };

  const handleApplyListDateRange = () => {
    if (!rangePickerDraft?.from || !rangePickerDraft?.to) {
      showErrorToast(
        new Error('Choose a start date and an end date to filter the list.'),
        'Incomplete date range',
      );
      return;
    }
    setListDateRange({ from: rangePickerDraft.from, to: rangePickerDraft.to });
    setDateRangeFilterOpen(false);
  };

  const handleClearListDateRange = () => {
    setListDateRange(undefined);
    setRangePickerDraft(undefined);
    setDateRangeFilterOpen(false);
  };


  const weekStatsPeriodLabel = dashboardDateRange ? 'This Range' : 'This Week';

  const summaryCards = [
    {
      id: 'today-total',
      period: 'Today',
      value: summaryStats.todayTotal,
      label: 'Total Appointments',
      icon: (
        <Box
          component="img"
          src={appointmentDateIcon}
          alt=""
          aria-hidden
          sx={{ width: 19, height: 19, objectFit: 'contain', display: 'block' }}
        />
      ),
    },
    {
      id: 'week-online',
      period: weekStatsPeriodLabel,
      value: summaryStats.thisWeekOnline,
      label: 'Online Sessions',
      icon: (
        <Box
          component="img"
          src={appointmentThisWeekOnlineSessionsIcon}
          alt=""
          aria-hidden
          sx={{ width: 19, height: 19, objectFit: 'contain', display: 'block' }}
        />
      ),
    },
    {
      id: 'week-in-person',
      period: weekStatsPeriodLabel,
      value: summaryStats.thisWeekInPerson,
      label: 'In-Person Sessions',
      icon: (
        <Box
          component="img"
          src={appointmentProfileIcon}
          alt=""
          aria-hidden
          sx={{ width: 19, height: 19, objectFit: 'contain', display: 'block' }}
        />
      ),
    },
    {
      id: 'week-scheduled',
      period: weekStatsPeriodLabel,
      value: summaryStats.thisWeekScheduled,
      label: 'Total Scheduled Sessions',
      icon: (
        <Box
          component="img"
          src={appointmentCalendarFilterIcon}
          alt=""
          aria-hidden
          sx={{ width: 19, height: 19, objectFit: 'contain', display: 'block' }}
        />
      ),
    },
  ];
  const selectedAppointmentRecord = selectedAppointment as AppointmentRecord | null;

  return (
    <Box data-demo-id="appointments-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: { xs: '16px', sm: '24px', lg: '28px' },
          border: '1px solid #dbe6eb',
          bgcolor: '#f8fafc',
          maxWidth: '100%',
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 3, md: 4 }, pt: { xs: 1.75, sm: 2.5 } }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 1.5, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Appointments
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 1.5, sm: 3, md: 4 }, pb: { xs: 4, md: 5 }, pt: { xs: 2.5, sm: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box sx={{ display: 'grid', gap: 0.95 }}>
              <Typography sx={pageTitleSx}>Appointments</Typography>
              <Typography sx={pageSubtitleSx}>
                {format(selectedDate || new Date(), 'EEE dd MMM')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
              <Button
                data-demo-id="appointments-create"
                data-action-tour-id="appointments-new"
                disableElevation
                onClick={() => handleOpenNewAppointmentFlow()}
                sx={{
                  borderRadius: '10px',
                  px: 2,
                  minHeight: 40,
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.84rem',
                  bgcolor: '#3BAEDB',
                  color: '#fff',
                  width: { xs: '100%', md: 'auto' },
                  '&:hover': {
                    bgcolor: '#2A9ECF',
                  },
                }}
              >
                New Appointment +
              </Button>
              <NotificationCenter buttonVariant="feature" />

              <Button
                data-action-tour-id="appointments-calendar"
                onClick={handleOpenCalendarView}
                sx={{
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  border: '1px solid #D0D5DD',
                  color: '#475467',
                  '&:hover': {
                    bgcolor: '#F8FAFC',
                    borderColor: '#BFC7D2',
                  },
                }}
              >
                <Box
                  component="img"
                  src={appointmentDateIcon}
                  alt=""
                  aria-hidden
                  sx={{ width: 22, height: 22, objectFit: 'contain', display: 'block' }}
                />
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))',
              },
              gap: 1.4,
            }}
          >
            {summaryCards.map((card) => (
              <Paper
                key={card.id}
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #E4E7EC',
                  px: 1.5,
                  py: 1.35,
                  bgcolor: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: '#EAF6FD',
                    }}
                  >
                    {card.icon}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        color: '#98A2B3',
                      }}
                    >
                      {card.period}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.9rem',
                        lineHeight: 1,
                        color: '#1D2939',
                      }}
                    >
                      {card.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.76rem',
                        color: '#667085',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {card.label}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          <Box
            sx={{
              mt: 2.5,
              borderBottom: '1px solid #E4E7EC',
              pb: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'stretch', lg: 'center' },
                justifyContent: 'space-between',
                gap: 1.2,
              }}
            >
              <Box
                data-action-tour-id="appointments-tabs"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.25, sm: 0.45 },
                  overflowX: { xs: 'auto', lg: 'visible' },
                }}
              >
                {tabOptions.map((tab) => {
                  const isActive = activeTab === tab.key;

                  return (
                    <Button
                      key={tab.key}
                      disableElevation
                      variant="text"
                      onClick={() => setActiveTab(tab.key)}
                      sx={{
                        minHeight: 44,
                        px: { xs: 1.1, sm: 1.35 },
                        borderRadius: 0,
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        color: isActive ? '#2FA8D5' : '#667085',
                        position: 'relative',
                        boxShadow: 'none',
                        gap: 0.68,
                        flexShrink: 0,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: '-1px',
                          height: '2px',
                          bgcolor: isActive ? '#2FA8D5' : 'transparent',
                        },
                        '&:hover': {
                          bgcolor: 'transparent',
                          color: isActive ? '#249CCB' : '#475467',
                        },
                      }}
                    >
                      {tab.label}
                      <Box
                        component="span"
                        sx={{
                          height: 18,
                          minWidth: 22,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.62,
                          borderRadius: '999px',
                          bgcolor: isActive ? '#2FA8D5' : '#EAECF0',
                          color: isActive ? '#FFFFFF' : '#667085',
                          fontWeight: 400,
                          fontSize: '0.75rem',
                          lineHeight: '18px',
                          transform: 'translateY(1px)',
                        }}
                      >
                        {tabCountsBadge[tab.key]}
                      </Box>
                    </Button>
                  );
                })}
              </Box>

              <Box data-action-tour-id="appointments-filters" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', lg: 'auto' } }}>
                <TextField
                  select
                  size="small"
                  value={modeFilter}
                  onChange={(event) => setModeFilter(event.target.value as ModeFilter)}
                  sx={{
                    minWidth: { xs: 0, sm: 124 },
                    flex: { xs: '1 1 140px', sm: '0 0 auto' },
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      borderRadius: '8px',
                      bgcolor: '#fff',
                    },
                    '& .MuiSelect-select': {
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.82rem',
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="in_person">In-person</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                </TextField>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: '8px',
                    ...(listDateRangeShortLabel
                      ? {
                          bgcolor: '#F0F9FF',
                          border: '1px solid #2FA8D5',
                        }
                      : {}),
                  }}
                >
                  <Popover open={dateRangeFilterOpen} onOpenChange={handleListDateRangePopoverChange}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        title={listDateRangeShortLabel || 'Filter by date range'}
                        aria-label={
                          listDateRangeShortLabel
                            ? `Date filter: ${listDateRangeShortLabel}`
                            : 'Filter appointments by date range'
                        }
                        sx={{
                          minWidth: 36,
                          width: listDateRangeShortLabel ? 'auto' : 36,
                          height: 36,
                          px: listDateRangeShortLabel ? 1.5 : 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          borderRadius: listDateRangeShortLabel ? '8px 0 0 8px' : '8px',
                          bgcolor: listDateRangeShortLabel ? 'transparent' : '#fff',
                          border: listDateRangeShortLabel ? 'none' : '1px solid #D0D5DD',
                          color: listDateRangeShortLabel ? '#0284C7' : '#475467',
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: listDateRangeShortLabel ? '#E0F2FE' : '#F8FAFC',
                            border: listDateRangeShortLabel ? 'none' : '1px solid #BFC7D2',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={appointmentCalendarFilterIcon}
                          alt=""
                          aria-hidden
                          sx={{ width: 16, height: 16, objectFit: 'contain', display: 'block' }}
                        />
                        {listDateRangeShortLabel && (
                          <Typography sx={{ fontSize: 13, fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                            {listDateRangeShortLabel}
                          </Typography>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={rangePickerDraft?.from ?? listDateRange?.from ?? new Date()}
                        selected={rangePickerDraft}
                        onSelect={setRangePickerDraft}
                        numberOfMonths={1}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          flexWrap: 'wrap',
                          borderTop: '1px solid #eef2f7',
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: '#475467', minWidth: 0 }}>
                          {rangePickerDraft?.from && rangePickerDraft?.to
                            ? `${format(
                                rangePickerDraft.from.getTime() <= rangePickerDraft.to.getTime()
                                  ? rangePickerDraft.from
                                  : rangePickerDraft.to,
                                'MMM d',
                              )} – ${format(
                                rangePickerDraft.from.getTime() <= rangePickerDraft.to.getTime()
                                  ? rangePickerDraft.to
                                  : rangePickerDraft.from,
                                'MMM d, yyyy',
                              )}`
                            : 'Select start and end dates'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={handleClearListDateRange}
                            sx={{ minWidth: 0, textTransform: 'none', fontSize: 12, color: '#667085' }}
                          >
                            Clear
                          </Button>
                          <Button
                            size="small"
                            disableElevation
                            onClick={handleApplyListDateRange}
                            sx={{
                              minWidth: 0,
                              textTransform: 'none',
                              fontSize: 12,
                              fontWeight: 600,
                              px: 1.5,
                              borderRadius: '8px',
                              bgcolor: '#3BAEDB',
                              color: '#fff',
                              '&:hover': { bgcolor: '#2A9ECF' },
                            }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    </PopoverContent>
                  </Popover>
                  {listDateRangeShortLabel ? (
                    <IconButton
                      type="button"
                      size="small"
                      onClick={handleClearListDateRange}
                      aria-label="Clear date range filter"
                      title="Clear date range"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '0 8px 8px 0',
                        color: '#0284C7',
                        '&:hover': { bgcolor: '#E0F2FE' },
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box data-action-tour-id="appointments-list" sx={{ mt: 2, display: 'grid', gap: 1.15 }}>
            {(loadingList || checkingCalendarStatus) ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #E4E7EC',
                  bgcolor: '#fff',
                  py: 8,
                  display: 'grid',
                  placeItems: 'center',
                  gap: 1.2,
                }}
              >
                <CircularProgress size={28} />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#667085' }}>
                  Loading appointments...
                </Typography>
              </Paper>
            ) : visibleAppointments.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '12px',
                  border: '1px dashed #D0D5DD',
                  bgcolor: '#fff',
                  py: 6,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#344054' }}>
                  No appointments found
                </Typography>
              </Paper>
            ) : (
              visibleAppointments.map((item) => (
                <AppointmentListCard
                  key={item.id}
                  startTime={item.startTime}
                  primaryLabel={item.clientName}
                  primaryLabelClientId={item.appointment.clientId}
                  title={item.title}
                  modeLabel={item.modeLabel}
                  status={item.status}
                  hideActions={shouldHideAppointmentJoinRescheduleCancel(item.status, item.endTime, {
                    startAt: item.startTime,
                    durationMinutes: item.appointment.duration,
                  })}
                  showJoin={
                    item.mode === 'online' &&
                    Boolean(item.meetingLink) &&
                    canJoinAppointment(item.status, item.startTime, item.endTime, {
                      startAt: item.startTime,
                      durationMinutes: item.appointment.duration,
                    })
                  }
                  onOpen={() => handleOpenDetailsFromList(item.appointment)}
                  onReschedule={(event) => handleRescheduleFromList(event, item.appointment)}
                  onJoin={(event) => {
                    event.stopPropagation();
                    window.open(item.meetingLink, '_blank', 'noopener,noreferrer');
                  }}
                  onCancel={(event) => {
                    void handleCancelFromList(event, item.appointment);
                  }}
                />
              ))
            )}

            {listPagination.total > 0 ? (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                <PaginationControls
                  currentPage={listPagination.page}
                  totalPages={Math.max(1, listPagination.totalPages)}
                  totalCount={listPagination.total}
                  pageSize={listPagination.limit}
                  onPageChange={handleListPageChange}
                  isLoading={loadingList}
                  summaryText={listPaginationSummary}
                  hideWhenSinglePage={false}
                  layout="footer"
                />
              </Box>
            ) : null}

          </Box>



          <ScheduleAppointmentDialog
            open={isScheduleDialogOpen}
            onClose={() => {
              setIsScheduleDialogOpen(false);
              setSchedulePrefill(undefined);
              setAppointmentToReschedule(null);
            }}
            prefill={schedulePrefill}
            appointmentIdToEdit={appointmentToReschedule}
            defaultClientId={selectedClient && selectedClient !== 'all' ? selectedClient : ''}
            onAppointmentCreated={reloadAppointments}
            onAppointmentUpdated={reloadAppointments}
          />

          <Modal
            open={createDialogOpen && !!selectedAppointment}
            onOpenChange={(open) => {
              updateCreateDialogState(open);
              if (!open) {
                setAppointmentModalFeedback(null);
                setSelectedAppointment(null);
                setSelectedTime(undefined);
              }
            }}
            title="Reschedule/Update Appointment"
            description="Update the appointment details below. Changes will be synced to your calendar."
            maxWidth="xl"
          >
            {appointmentModalFeedback ? (
              <div className="mb-4">
                <InlineFeedback
                  variant="error"
                  title={appointmentModalFeedback.title}
                  message={appointmentModalFeedback.message}
                />
              </div>
            ) : null}
            <AppointmentForm
              key={selectedAppointment?.id || `new-${selectedTime || Date.now()}`}
              onSubmit={handleCreateAppointment}
              defaultValues={getAppointmentFormDefaultValues(
                selectedAppointment,
                selectedDate,
                selectedTime,
              )}
              disabledDates={[]}
              isEditing={!!selectedAppointment}
              isRecurring={selectedAppointment?.isRecurring || false}
              isModified={
                selectedAppointmentRecord?.recurringDetails?.isModified === true
              }
              isParent={
                selectedAppointmentRecord?.recurringDetails?.isParent === true
              }
              onDelete={selectedAppointment ? handleDeleteAppointment : undefined}
              appointmentId={selectedAppointment?.id}
              appointment={selectedAppointment}
            />
          </Modal>

          <Modal
            open={appointmentDetailOpen}
            onOpenChange={(open) => {
              setAppointmentDetailOpen(open);
              if (!open && selectedAppointment) {
                setSelectedAppointment(null);
              }
            }}
            title=""
            maxWidth="md"
          >
            {selectedAppointment && (
              <AppointmentDetails
                appointment={selectedAppointment}
                client={selectedAppointmentClient}
                onEdit={handleEditAppointment}
                onDelete={handleDeleteAppointment}
              />
            )}
          </Modal>

          <CancelAppointmentDialog
            open={!!cancelAppointmentItem}
            onOpenChange={(open) => {
              if (!open) {
                setCancelAppointmentFeedback(null);
                setCancelAppointmentItem(null);
              }
            }}
            appointment={cancelAppointmentItem}
            clientName={cancelAppointmentClientName}
            isRecurring={Boolean(cancelAppointmentItem?.isRecurring)}
            isModified={cancelAppointmentRecord?.recurringDetails?.isModified === true}
            isParent={cancelAppointmentRecord?.recurringDetails?.isParent === true}
            isCancelling={isCancelling}
            error={cancelAppointmentFeedback}
            onConfirm={handleConfirmCancelAppointment}
          />
        </Box>
      </Paper>
      <ActionTour
        ariaLabel="Appointments action guide"
        label="Appointments guide"
        storageKey="caloriescience:appointments-action-guide:v1"
        steps={appointmentsActionTourSteps}
      />
    </Box>
  );
};

export default AppointmentsPage;
